import { Body, Eyebrow, RadioCard, RadioCardGroup, Stack } from '@leanin/ui';
import { useTranslations } from '@/i18n/translations';

// The content-post-floor radio group on the Space Visibility tab — who can
// start posts. Orthogonal to the access tier: access decides who can find
// the Space and open its page, this decides who can publish into its feed.
// Circles and Networks don't get this control: their content is members-only
// by product decision, so posting is members-only too. Renders bare (no Card
// chrome) like its siblings so the Visibility tab composes access + content
// + join into a single card.
//
// `leader` is a valid stored value but deliberately has no card: a Space
// where members can't post is an announcement shape reached via the API,
// not a picker. Rows already set to `leader` render with nothing selected
// plus an explanatory note, and keep their value until the leader actively
// picks one of the offered floors.

export type AdminPostFloor = 'anyone' | 'member' | 'leader';

type Props = {
  readonly value: AdminPostFloor;
  readonly onChange: (next: Exclude<AdminPostFloor, 'leader'>) => void;
  /** "space" — wordings reference the entity by name. */
  readonly entityLabel: string;
  /** Hide the `anyone` card. A private group open to every poster is a
   *  contradiction the DB forbids (`groups_post_floor_not_below_read_floor`
   *  with the access-derived read floor) and the server rejects — the tab
   *  omits the card whenever the selected access tier is `private` so the
   *  pair is unreachable from this surface. */
  readonly omitAnyone?: boolean;
};

export function AdminPostFloorChoice({
  value,
  onChange,
  entityLabel,
  omitAnyone,
}: Props) {
  const t = useTranslations('Group');
  const kind = entityLabel.toLowerCase();
  const allOptions: ReadonlyArray<{
    value: Exclude<AdminPostFloor, 'leader'>;
    label: string;
    lede: string;
  }> = [
    {
      value: 'anyone',
      label: t('admin.postFloor.anyoneLabel'),
      lede: t('admin.postFloor.anyoneLede', { kind }),
    },
    {
      value: 'member',
      label: t('admin.postFloor.memberLabel'),
      lede: t('admin.postFloor.memberLede', { kind }),
    },
  ];
  const options = omitAnyone
    ? allOptions.filter((o) => o.value !== 'anyone')
    : allOptions;

  return (
    <Stack gap="md">
      <Eyebrow tone="accent">{t('admin.postFloor.eyebrow')}</Eyebrow>
      {value === 'leader' ? (
        <Body size="sm" color="mute">
          {t('admin.postFloor.leaderNote', { kind })}
        </Body>
      ) : null}
      <RadioCardGroup
        value={value}
        onValueChange={(v) => onChange(v as Exclude<AdminPostFloor, 'leader'>)}
        aria-label={t('admin.postFloor.ariaLabel', { kind })}
      >
        {options.map((opt) => (
          <RadioCard
            key={opt.value}
            value={opt.value}
            title={opt.label}
            sub={opt.lede}
          />
        ))}
      </RadioCardGroup>
    </Stack>
  );
}
